/**
 * FetchChatNodesUseCase
 *
 * Paginated fetch of chat NODES (the inbox direct-messages list) with per-row
 * E2EE decryption of `text`-type rows' `lastContent`. Produces
 * `ChatMessageNodeEntity[]` newest-first (the page order from the service,
 * which is desc by `timestamp`) and the cursor for the next page.
 *
 * Flutter source: lib/presentation/screens/messages/messages_screen.dart:237-321
 * (`_getChatItems`).
 *   - Query/pagination handled by `ChatService.fetchChatNodesPage` (over
 *     `firestoreService.getChatNodesPage`).
 *   - messages_screen.dart:294-307 — for each `text`-type node ONLY, the peer
 *     is resolved (`peerUserId = _myUser.firebaseId == node.idFrom ? node.idTo
 *     : node.idFrom`), the peer `UserEntity` is fetched, and IF the peer
 *     exists the E2EE key is derived from `peerUser.publicJwk` and
 *     `node.lastContent` is decrypted in place. Non-`text` nodes pass through
 *     untouched. We mirror this exact ordering: resolve peer → `GetUserUseCase`
 *     → `InitializeTextEncryptionUseCase` → `DecryptTextUseCase`.
 *   - `hasMore` semantics (`docs.length < limit ⇒ false`,
 *     messages_screen.dart:267-269) are derived by the consuming hook (Task 9),
 *     not here.
 *
 * Pure-domain contract:
 *   - The use case owns the peer-resolve + decrypt; the service stays a thin
 *     fetch+remap. The decrypt trio is invoked exactly as the existing chat
 *     read use cases do (`subscribeToChatUpdatesUseCase`,
 *     `fetchChatMessagesUseCase`): the singleton's AES-GCM key is re-derived
 *     per peer via `InitializeTextEncryptionUseCase` immediately before each
 *     `DecryptTextUseCase`, so interleaved peers do not clobber one another.
 *   - The use case does NOT touch `firestoreService` directly and does NOT
 *     dispatch to Redux — both are presentation concerns.
 */

import { ChatService } from '@/domain/services/chat/chatService';
import { GetUserUseCase, getUserUseCase } from '@/domain/usecases/user/getUserUseCase';
import {
  InitializeTextEncryptionUseCase,
  initializeTextEncryptionUseCase,
} from '@/domain/usecases/textEncryption/initializeTextEncryptionUseCase';
import {
  DecryptTextUseCase,
  decryptTextUseCase,
} from '@/domain/usecases/textEncryption/decryptTextUseCase';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';

export interface FetchChatNodesResult {
  nodes: ChatMessageNodeEntity[];
  /**
   * The timestamp of the last (oldest, in this desc-ordered page) node in the
   * returned page, or `null` when the page is empty. Pass this back to the next
   * `execute` call as `lastTimestamp` to advance the cursor.
   */
  lastTimestamp: number | null;
}

export class FetchChatNodesUseCase {
  private chatService: ChatService;
  private getUserUseCase: GetUserUseCase;
  private initializeTextEncryptionUseCase: InitializeTextEncryptionUseCase;
  private decryptTextUseCase: DecryptTextUseCase;

  constructor(
    chatService: ChatService = ChatService.getInstance(),
    getUser: GetUserUseCase = getUserUseCase,
    initializeEncryption: InitializeTextEncryptionUseCase = initializeTextEncryptionUseCase,
    decryptUseCase: DecryptTextUseCase = decryptTextUseCase,
  ) {
    this.chatService = chatService;
    this.getUserUseCase = getUser;
    this.initializeTextEncryptionUseCase = initializeEncryption;
    this.decryptTextUseCase = decryptUseCase;
  }

  /**
   * Fetch one page of chat nodes and decrypt each `text`-type node's
   * `lastContent` in place. Non-`text` nodes are returned untouched.
   *
   * @param userId - The current user's Firebase id (used both as the query's
   *   `array-contains` member and to resolve the peer side of each node).
   * @param lastTimestamp - The timestamp of the last node from the previous
   *   page, or `null` for the first page.
   * @param limit - Page size (defaults to
   *   `SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION = 20`, matching Flutter
   *   `app_constants.dart:11`).
   */
  async execute(
    userId: string,
    lastTimestamp: number | null,
    limit: number = SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION,
  ): Promise<FetchChatNodesResult> {
    const { nodes, lastTimestamp: nextLastTimestamp } =
      await this.chatService.fetchChatNodesPage(userId, lastTimestamp, limit);

    // Decrypt per node, serially. Flutter decrypts serially in a for-loop
    // (messages_screen.dart:288-310), and serialization is REQUIRED here: the
    // encryption singleton has a single derived-key slot, so the
    // initialize→decrypt pair for one peer must complete before the next peer's
    // initialize re-derives the key (parity with `ShareWithFriendsUseCase`'s
    // per-peer serialization rationale).
    for (const node of nodes) {
      // Decrypt ONLY a `text`-type node with non-null, non-empty content. The
      // predicate (and the rationale for excluding the share types) lives on
      // `ChatUtils.shouldDecryptNodeContent`, shared with
      // `SubscribeToChatNodesUseCase`.
      if (!ChatUtils.shouldDecryptNodeContent(node.type, node.lastContent)) {
        continue;
      }

      // Resolve the peer: the OTHER user on the node. messages_screen.dart:295-298.
      const peerId = userId === node.idFrom ? node.idTo : node.idFrom;

      // Fetch the peer user; decrypt only if it exists (messages_screen.dart:299-306
      // — `if (peerUser != null) { ... decrypt ... }`).
      const peerUser = await this.getUserUseCase.execute(peerId);
      if (peerUser == null) {
        continue;
      }

      // Re-derive the AES-GCM key for this peer, then decrypt in place.
      await this.initializeTextEncryptionUseCase.execute(
        (peerUser.publicJwk as JsonWebKey | undefined) ?? null,
      );
      node.lastContent = await this.decryptTextUseCase.execute(node.lastContent);
    }

    return { nodes, lastTimestamp: nextLastTimestamp };
  }
}

export const fetchChatNodesUseCase = new FetchChatNodesUseCase();
